// src/App.tsx
import { useState, useEffect, useCallback } from 'react';
import bookshopData from '../data/processed/map-data.json';
import cityStatsData from '../data/processed/city-stats.json';
import { MapData, CityStatsData, Bookshop } from './types';
import { DensityMap } from './components/DensityMap';
import { CityStats } from './components/CityStats';
import { About } from './components/About';
import CityRankings from './components/CityRankings';
import { ChevronDown } from 'lucide-react';
import { SEOConfig } from './components/SEOConfig';

// Type guards
function isMapData(data: unknown): data is MapData {
  const mapData = data as MapData;
  return (
    typeof mapData === 'object' &&
    mapData !== null &&
    'cities' in mapData &&
    typeof mapData.cities === 'object'
  );
}

function isCityStatsData(data: unknown): data is CityStatsData {
  const statsData = data as CityStatsData;
  return (
    typeof statsData === 'object' &&
    statsData !== null &&
    'cities' in statsData &&
    typeof statsData.cities === 'object'
  );
}

function App() {
  // Validate data format
  if (!isMapData(bookshopData) || !isCityStatsData(cityStatsData)) {
    throw new Error('Invalid data format');
  }

  const data: MapData = bookshopData;
  const statsData: CityStatsData = cityStatsData;
  
  const cities = Object.keys(data.cities).sort();
  const [selectedCity, setSelectedCity] = useState<string>(cities[0] || '');
  const [focusLocation, setFocusLocation] = useState<string | null>(null);
  const [view, setView] = useState<'city' | 'about'>('city');
  const [mapInteracted, setMapInteracted] = useState(false);

  // Get current city data
  const cityData = selectedCity ? data.cities[selectedCity] : null;
  const cityStats = selectedCity ? statsData.cities[selectedCity].stats : null;

  // Function to update URL when city changes
  const updateURL = useCallback((city: string) => {
    // Replace spaces with hyphens and remove special characters
    const cityPath = city.toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with hyphens
      .replace(/[^a-z0-9-]/g, '');    // Remove special characters
    if (window.history.pushState) {
      window.history.pushState({ city }, '', `/${cityPath}`);
    }
  }, []);

  // Function to get city from URL
  const getCityFromURL = useCallback(() => {
    const path = window.location.pathname.slice(1); // Remove leading slash
    if (path === 'about') {
      setView('about');
      return null;
    }
    
    // Convert URL format back to city name format for comparison
    const normalizedPath = decodeURIComponent(path).toLowerCase();
    
    const cityFromPath = cities.find(city => {
      const normalizedCity = city.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      return normalizedCity === normalizedPath;
    });
    
    return cityFromPath || cities[0];
  }, [cities]);

  // Initialize selected city from URL
  useEffect(() => {
    const initialCity = getCityFromURL();
    if (initialCity) {
      setSelectedCity(initialCity);
    }
  }, [getCityFromURL]);

  // Handle city selection
  const handleCityChange = (newCity: string) => {
    setSelectedCity(newCity);
    updateURL(newCity);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const cityFromURL = getCityFromURL();
      if (cityFromURL) {
        setSelectedCity(cityFromURL);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [getCityFromURL]);

  const handleClusterClick = useCallback((location: string) => {
    console.log('Cluster clicked, location:', location);
    console.log('Current mapInteracted state:', mapInteracted);
    
    setMapInteracted(false);
    
    const shops = cityData?.bookshops.filter(shop => shop.address === location);
    if (shops?.length) {
      const avgLat = shops.reduce((sum, shop) => sum + shop.latitude, 0) / shops.length;
      const avgLng = shops.reduce((sum, shop) => sum + shop.longitude, 0) / shops.length;
      const newLocation = `${avgLng},${avgLat}`;
      console.log('Setting new focus location:', newLocation);
      setFocusLocation(newLocation);
    }
  }, [cityData, mapInteracted]);

  const handleStreetClick = useCallback((shop: Bookshop) => {
    setMapInteracted(false);
    setFocusLocation(`${shop.longitude},${shop.latitude}`);
  }, []);

  const handleMapInteraction = useCallback(() => {
    if (!mapInteracted) {
      console.log('Map interaction detected');
      setMapInteracted(true);
    }
  }, [mapInteracted]);

  useEffect(() => {
    console.log('Selected city changed, resetting states');
    setFocusLocation(null);
    setMapInteracted(false);
  }, [selectedCity]);

  if (!cityData || !cityStats) {
    return (
      <>
        <SEOConfig
          title="Loading - Bookmap City"
          description="Loading bookstore data..."
          cityStats={null}
        />
        <div className="text-zinc-400" role="alert" aria-busy="true">Loading city data...</div>
      </>
    );
  }

  if (view === 'about') {
    return (
      <>
        <SEOConfig
          title="About"
          description="Learn about our mission to help you discover local independent bookstores."
          canonicalPath="/about"
          type="article"
        />
        <About onBack={() => {
          setView('city');
          if (selectedCity) {
            updateURL(selectedCity);
          }
        }} />
      </>
    );
  }

  const cityCenter = selectedCity === 'Milan' 
      ? {
          longitude: 9.182885830569914,
          latitude: 45.465078830851056
        }
      : selectedCity === 'Montevideo'
      ? {
          longitude: -56.19530789136347,
          latitude: -34.904513065673086
        }
      : cityData.bookshops.length > 0
        ? {
            longitude: cityData.bookshops.reduce((sum, shop) => sum + shop.longitude, 0) / cityData.bookshops.length,
            latitude: cityData.bookshops.reduce((sum, shop) => sum + shop.latitude, 0) / cityData.bookshops.length,
          }
        : null;
  
  const mapData = {
    bookshops: cityData.bookshops,
    center: cityCenter,
    focusLocation: focusLocation,
    selectedCity: selectedCity
  };

  const pageDescription = cityStats 
    ? `Discover ${cityStats.total} bookstores in ${selectedCity}. Find independent bookshops, literary clusters, and explore the city's reading culture. Updated ${new Date().toLocaleDateString()}.`
    : 'Explore independent bookstores across major cities. Find local bookshops and discover literary havens in your area.';

  return (
    <>
      <SEOConfig 
        title={`Bookstores in ${selectedCity}`}
        description={pageDescription}
        canonicalPath={`/${selectedCity.toLowerCase()}`}
        cityStats={cityStats}
        imageUrl={`/assets/bookstores/${selectedCity.toLowerCase()}/hero.jpg`}
      />

      <main 
        className="min-h-screen bg-zinc-900 pt-8 px-4 md:px-8" 
        role="main"
        aria-label={`Bookstore explorer for ${selectedCity}`}
      >
        <div className="max-w-6xl mx-auto font-mono relative">
          <button 
            onClick={() => {
              setView('about');
              updateURL('about');
            }}
            className="absolute top-[10px] right-0 text-sm text-zinc-500 hover:text-zinc-300 transition-colors z-10"
            aria-label="View about page"
          >
            About
          </button>
          
          <header className="mb-8 md:mb-16" role="banner">
            <h1 className="sr-only">Bookmap City - {selectedCity}</h1>
            <div className="flex items-start">
              <div className="relative w-[240px] md:w-[340px] lg:w-[440px]">
                <label htmlFor="city-select" className="sr-only">Select a city</label>
                <select 
                  id="city-select"
                  value={selectedCity}
                  onChange={(e) => handleCityChange(e.target.value)}
                  className="w-full appearance-none bg-transparent 
                           text-2xl md:text-4xl lg:text-5xl font-light 
                           text-zinc-100 tracking-tight border-none outline-none 
                           cursor-pointer hover:text-zinc-300 transition-colors 
                           focus:ring-0 focus:outline-none py-2"
                  aria-label="Select a city to explore"
                >
                  {cities.map(city => (
                    <option 
                      key={city} 
                      value={city}
                      className="bg-zinc-800 text-base"
                    >
                      {city}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 left-[200px] md:left-[300px] lg:left-[400px] flex items-center">
                  <ChevronDown className="h-4 w-4 text-zinc-400" aria-hidden="true" />
                </div>
              </div>
            </div>
            <div className="flex space-x-8 text-sm text-zinc-400 mt-2">
              <p>{cityStats?.total || cityData.bookshops.length} bookshops</p>
            </div>
          </header>

          <div className="space-y-8 md:space-y-16">
            <section 
              aria-label="City Statistics"
              id="city-stats"
            >
              <CityStats 
                stats={statsData.cities[selectedCity].stats}
                streets={statsData.cities[selectedCity].streets}
                onClusterClick={handleClusterClick}
                onStreetClick={handleStreetClick}
              />
            </section>
            
            <section 
              aria-label="Interactive Map"
              id="interactive-map"
            >
              <DensityMap 
                data={mapData} 
                focusZoom={16}
                onMapInteraction={handleMapInteraction}
                mapInteracted={mapInteracted}
              />
            </section>

            <section 
              aria-label="City Rankings"
              id="city-rankings"
            >
              <CityRankings
                selectedCity={selectedCity}
                cityStats={cityStatsData.cities}
              />
            </section>
          </div>
        </div>
      </main>
    </>
  );
}

export default App;